
import React, { useState, useEffect, useCallback } from 'react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { X, Check, Star, Loader2, Sparkles, Map as MapIcon, Mountain, Download, CreditCard, Smartphone, ChevronLeft, Clock, Bell, RotateCcw, Fish, Navigation, TreePine, Users, Car, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createStripeCheckoutSession, createStripePremiumCheckoutSession, createVippsAgreementSession, createVippsPremiumAgreementSession, fetchSystemConfig, updateUserSettings } from '../services/firebaseService';
import { isIAPPlatform, identifyIAP, getMonthlyPackage, getPremiumPackage, purchasePackage, restorePurchases, hasActiveEntitlement, hasActivePremiumEntitlement } from '../services/iapService';
import { AppUser } from '../types';
import type { PremiumConfig } from './admin/panels/AdminPremiumPanel';
import { useToast } from '../contexts/ToastContext';

interface UpgradeOverlayProps {
    user: AppUser;
    onClose: () => void;
    onSelectFree?: () => void;
    vippsEnabled?: boolean;
    testerApplicationsEnabled?: boolean;
    onApplyTester?: () => void;
}

const UpgradeOverlay: React.FC<UpgradeOverlayProps> = ({ user, onClose, onSelectFree, vippsEnabled = false, testerApplicationsEnabled = false, onApplyTester }) => {
    const { t, i18n } = useTranslation();
    const toast = useToast();
    const trapRef = useFocusTrap<HTMLDivElement>();
    const [loading, setLoading] = useState(false);
    const [dynamicPrice, setDynamicPrice] = useState<number | null>(null);
    const isIOS = isIAPPlatform();

    // Premium tier state
    const [premiumConfig, setPremiumConfig] = useState<PremiumConfig | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<'supporter' | 'premium'>('supporter');

    // RevenueCat state (iOS only)
    const [iapPackage, setIapPackage] = useState<any | null>(null);
    const [iapPremiumPackage, setIapPremiumPackage] = useState<any | null>(null);
    const [iapPriceLabel, setIapPriceLabel] = useState<string | null>(null);
    const [iapPremiumPriceLabel, setIapPremiumPriceLabel] = useState<string | null>(null);
    const [restoring, setRestoring] = useState(false);
    const [iapError, setIapError] = useState<string | null>(null);

    useEffect(() => {
        // Load premium config
        fetchSystemConfig('premium_config').then(data => {
            if (data?.enabled) setPremiumConfig(data as PremiumConfig);
        }).catch(() => {});

        if (isIOS) {
            // Fetch Apple IAP offerings
            identifyIAP(user.uid).then(async () => {
                const pkg = await getMonthlyPackage();
                if (pkg) {
                    setIapPackage(pkg);
                    setIapPriceLabel(
                        `${pkg.product.priceString}/${i18n.language === 'en' ? 'mo' : i18n.language === 'de' ? 'Mon.' : 'mnd'}`
                    );
                }
                const premPkg = await getPremiumPackage();
                if (premPkg) {
                    setIapPremiumPackage(premPkg);
                    setIapPremiumPriceLabel(
                        `${premPkg.product.priceString}/${i18n.language === 'en' ? 'mo' : i18n.language === 'de' ? 'Mon.' : 'mnd'}`
                    );
                }
            }).catch((err) => { console.error('IAP package load error:', err); });
        } else {
            // Fetch Firestore price for Stripe/Vipps
            fetchSystemConfig('subscription_config').then(data => {
                if (data?.priceNok && Number.isFinite(data.priceNok)) {
                    setDynamicPrice(data.priceNok);
                }
            }).catch(() => { });
        }
    }, [isIOS, i18n.language]);

    const priceLabel = isIOS
        ? (iapPriceLabel ?? t('upgrade.pricing.paid_price'))
        : (dynamicPrice
            ? `kr ${dynamicPrice}/${i18n.language === 'en' ? 'mo' : i18n.language === 'de' ? 'Mon.' : 'mnd'}`
            : t('upgrade.pricing.paid_price'));

    const premiumPriceLabel = isIOS
        ? (iapPremiumPriceLabel ?? (premiumConfig ? `kr ${premiumConfig.priceNok}/${i18n.language === 'en' ? 'mo' : 'mnd'}` : null))
        : (premiumConfig ? `kr ${premiumConfig.priceNok}/${i18n.language === 'en' ? 'mo' : i18n.language === 'de' ? 'Mon.' : 'mnd'}` : null);

    const [pendingChoice, setPendingChoice] = useState<'free' | 'paid' | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'vipps' | 'apple' | null>(null);

    // ── Payment handlers ────────────────────────────────────────────

    const handleStripeSubscribe = async () => {
        setLoading(true);
        try {
            const createFn = selectedPlan === 'premium' ? createStripePremiumCheckoutSession : createStripeCheckoutSession;
            const { url } = await createFn(window.location.href);
            window.location.href = url;
        } catch (error) {
            console.error("Checkout error:", error);
            toast(t('upgrade.errors.payment_failed'), 'error');
            setLoading(false);
        }
    };

    const handleVippsSubscribe = async () => {
        setLoading(true);
        try {
            const createFn = selectedPlan === 'premium' ? createVippsPremiumAgreementSession : createVippsAgreementSession;
            const { vippsConfirmationUrl } = await createFn(window.location.href);
            window.location.href = vippsConfirmationUrl;
        } catch (error) {
            console.error("Vipps checkout error:", error);
            toast(t('upgrade.errors.payment_failed'), 'error');
            setLoading(false);
        }
    };

    const handleAppleSubscribe = async () => {
        setLoading(true);
        setIapError(null);
        try {
            const isPremium = selectedPlan === 'premium';
            let pkg = isPremium ? iapPremiumPackage : iapPackage;
            if (!pkg) {
                await identifyIAP(user.uid);
                pkg = isPremium ? await getPremiumPackage() : await getMonthlyPackage();
                if (pkg) isPremium ? setIapPremiumPackage(pkg) : setIapPackage(pkg);
            }
            if (!pkg) {
                setIapError('Ingen abonnementspakke tilgjengelig. Prøv igjen om litt.');
                return;
            }
            const customerInfo = await purchasePackage(pkg);
            if (hasActiveEntitlement(customerInfo)) {
                const plan = hasActivePremiumEntitlement(customerInfo) ? 'premium' : 'supporter';
                await updateUserSettings(user.uid, { subscriptionStatus: 'paid', subscriptionPlan: plan });
                onClose();
            }
        } catch (error: any) {
            console.error("Apple IAP error:", error);
            if (error?.userCancelled !== true) {
                setIapError(t('upgrade.errors.payment_failed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRestorePurchases = async () => {
        setRestoring(true);
        setIapError(null);
        try {
            const customerInfo = await restorePurchases();
            if (hasActiveEntitlement(customerInfo)) {
                await updateUserSettings(user.uid, { subscriptionStatus: 'paid' });
                onClose();
            } else {
                setIapError(t('upgrade.errors.no_purchases_to_restore'));
            }
        } catch (error) {
            console.error("Restore error:", error);
            setIapError(t('upgrade.errors.restore_failed'));
        } finally {
            setRestoring(false);
        }
    };

    const handleConfirm = () => {
        if (pendingChoice === 'free') {
            onSelectFree?.();
            setPendingChoice(null);
        } else if (pendingChoice === 'paid') {
            if (paymentMethod === 'apple') {
                handleAppleSubscribe();
            } else if (paymentMethod === 'vipps') {
                handleVippsSubscribe();
            } else {
                handleStripeSubscribe();
            }
        }
    };

    // ── Payment method selection (non-iOS only) ─────────────────────
    const renderPaymentMethodSelection = () => (
        <div className="flex-1 flex flex-col justify-center items-center text-center space-y-6 animate-in fade-in zoom-in-95 duration-300">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                <CreditCard size={32} />
            </div>

            <h3 className="text-xl font-black text-[var(--text-primary)] uppercase tracking-tight">
                {t('upgrade.payment_method.title')}
            </h3>

            <div className="w-full space-y-3 max-w-sm">
                {/* Stripe / Card */}
                <button
                    onClick={() => { setPaymentMethod('stripe'); setPendingChoice('paid'); }}
                    className="w-full p-5 rounded-2xl border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] hover:border-indigo-500/30 transition-all active:scale-95 flex items-center gap-4 text-left group"
                >
                    <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                        <CreditCard size={22} className="text-indigo-400" />
                    </div>
                    <div>
                        <p className="text-sm font-black text-[var(--text-primary)] uppercase tracking-wide">{t('upgrade.payment_method.stripe_title')}</p>
                        <p className="text-2xs text-[var(--text-muted)] font-medium">{t('upgrade.payment_method.stripe_subtitle')}</p>
                    </div>
                </button>

                {/* Vipps */}
                <div className="relative">
                    <button
                        onClick={vippsEnabled ? () => { setPaymentMethod('vipps'); setPendingChoice('paid'); } : undefined}
                        disabled={!vippsEnabled}
                        className={`w-full p-5 rounded-2xl border flex items-center gap-4 text-left transition-all ${vippsEnabled
                            ? 'border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] hover:border-vipps/30 active:scale-95 group cursor-pointer'
                            : 'border-[var(--border-primary)] bg-[var(--bg-surface-alt)] cursor-default'
                            }`}
                    >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${vippsEnabled ? 'bg-vipps/10' : 'bg-[var(--bg-surface-alt)]'}`}>
                            <Smartphone size={22} className={vippsEnabled ? 'text-vipps' : 'text-[var(--text-muted)]'} />
                        </div>
                        <div className={vippsEnabled ? '' : 'opacity-40'}>
                            <p className="text-sm font-black text-[var(--text-primary)] uppercase tracking-wide">{t('upgrade.payment_method.vipps_title')}</p>
                            <p className="text-2xs text-[var(--text-muted)] font-medium">{t('upgrade.payment_method.vipps_subtitle')}</p>
                        </div>
                    </button>
                    {!vippsEnabled && (
                        <div className="absolute inset-0 rounded-2xl flex items-center justify-end pr-5">
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-2xs font-black uppercase tracking-widest">
                                <Clock size={12} /> {t('upgrade.payment_method.coming_soon')}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <button
                onClick={() => setPendingChoice(null)}
                className="flex items-center gap-1 text-[var(--text-muted)] hover:text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest transition-colors pt-2"
            >
                <ChevronLeft size={14} /> {t('upgrade.payment_method.btn_back')}
            </button>
        </div>
    );

    // ── Confirmation step ───────────────────────────────────────────
    const renderConfirmation = () => (
        <div className="flex-1 flex flex-col justify-center items-center text-center space-y-8 animate-in fade-in zoom-in-95 duration-300">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                {pendingChoice === 'paid' ? <Star size={40} /> : <Check size={40} />}
            </div>

            <div className="space-y-4 max-w-sm">
                <h3 className="text-2xl font-black text-[var(--text-primary)] uppercase tracking-tight">
                    {pendingChoice === 'free' ? t('upgrade.confirm.free_title') : t('upgrade.confirm.paid_title')}
                </h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                    {pendingChoice === 'free' ? (
                        t('upgrade.confirm.free_description')
                    ) : paymentMethod === 'apple' ? (
                        t('upgrade.confirm.paid_description_apple')
                    ) : paymentMethod === 'vipps' ? (
                        t('upgrade.confirm.paid_description_vipps')
                    ) : (
                        t('upgrade.confirm.paid_description')
                    )}
                </p>
            </div>

            {iapError && (
                <p className="text-xs text-red-400 font-medium">{iapError}</p>
            )}

            {/* Legal links — required by Apple 3.1.2(c) */}
            <p className="text-2xs text-[var(--text-muted)] text-center font-medium leading-relaxed">
                {pendingChoice === 'paid' && paymentMethod === 'apple' && (
                    <><span>{t('upgrade.legal.subscription_terms')}</span><br /></>
                )}
                <a href="/vilkar" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--text-secondary)]">{t('upgrade.legal.terms')}</a>
                {' · '}
                <a href="/personvern" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--text-secondary)]">{t('upgrade.legal.privacy')}</a>
            </p>

            <div className="flex flex-col w-full gap-3 pt-4">
                <button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-4 rounded-2xl text-2xs font-black uppercase tracking-widest shadow-lg transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
                >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                    {t('upgrade.confirm.btn_confirm')}
                </button>
                <button
                    onClick={() => { setPendingChoice(null); setPaymentMethod(null); setIapError(null); }}
                    className="w-full bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] py-4 rounded-2xl text-2xs font-black uppercase tracking-widest transition-all active:scale-95"
                >
                    {t('upgrade.confirm.btn_back')}
                </button>
            </div>
        </div>
    );

    const freeFeatures: string[] = [
        'save_spots', 'see_spots', 'simple_map', 'reviews', 'badges',
        'road_layers', 'free_layers', 'conditions', 'packing', 'vehicle'
    ];

    const paidCategories: { key: string; icon: React.ReactNode; features: string[] }[] = [
        { key: 'maps', icon: <MapIcon size={14} />, features: ['satellite_map', 'trails_list', 'precipitation', 'property', 'offline_maps'] },
        { key: 'navigation', icon: <Navigation size={14} />, features: ['gps_tracking', 'route_planner', 'flyover_3d', 'route_follow', 'gpx_download'] },
        { key: 'nature', icon: <TreePine size={14} />, features: ['wildlife', 'fishing', 'webcams', 'dump_stations', 'bathing_places', 'coastal'] },
        { key: 'collaboration', icon: <Users size={14} />, features: ['collab_maps', 'drawing', 'content_folders', 'notifications'] },
    ];

    return (
        <div className="fixed left-0 right-0 bottom-0 z-[10010] flex items-end md:items-center justify-center md:px-4 md:pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
            <div ref={trapRef} role="dialog" aria-modal="true" className="relative w-full max-w-5xl bg-[var(--bg-base)] border border-[var(--border-secondary)] rounded-t-panel md:rounded-modal shadow-3xl flex flex-col max-h-[95dvh] md:max-h-[90dvh] animate-in slide-in-from-bottom-4 md:zoom-in-95 duration-300" style={{ overscrollBehavior: 'contain' }}>

                {/* Close Button */}
                <button onClick={onClose} className="absolute top-3 right-3 z-50 p-2 bg-black/50 md:bg-[var(--bg-surface-alt)] text-white md:text-[var(--text-muted)] md:hover:text-[var(--text-primary)] rounded-full md:rounded-xl transition-colors">
                    <X size={18} />
                </button>

                {pendingChoice === 'paid' && paymentMethod ? (
                    <div className="flex-1 overflow-y-auto p-6 md:p-8" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
                        {renderConfirmation()}
                    </div>
                ) : pendingChoice === 'paid' && !paymentMethod && !isIOS ? (
                    <div className="flex-1 overflow-y-auto p-6 md:p-8" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
                        {renderPaymentMethodSelection()}
                    </div>
                ) : pendingChoice === 'free' ? (
                    <div className="flex-1 overflow-y-auto p-6 md:p-8" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
                        {renderConfirmation()}
                    </div>
                ) : (
                    <>
                        {/* Banner image */}
                        <div className="relative h-32 md:h-44 shrink-0 rounded-t-panel md:rounded-t-modal overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-emerald-700 to-stone-900" />
                            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-[var(--bg-base)]" />
                            <div className="absolute bottom-4 left-5 md:left-8 z-10">
                                <h2 className="text-xl md:text-2xl font-black text-white uppercase tracking-tighter drop-shadow-lg">Fricamp</h2>
                                <p className="text-emerald-200 text-tiny md:text-xs font-bold mt-0.5 drop-shadow">{t('upgrade.supporter_description')}</p>
                            </div>
                        </div>

                        {/* Scrollable content area */}
                        <div className="flex-1 overflow-y-auto min-h-0 px-4 md:px-8 pb-4 md:pb-6 space-y-4" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
                            {/* All tiers side by side */}
                            <div className={`grid grid-cols-1 gap-3 md:gap-4 -mt-2 ${premiumConfig ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                                {/* Free Tier */}
                                <div
                                    onClick={() => onSelectFree && setPendingChoice('free')}
                                    className={`p-4 md:p-5 rounded-2xl border flex flex-col gap-2 md:gap-3 relative transition-all ${onSelectFree ? 'cursor-pointer hover:border-[var(--border-secondary)] hover:bg-[var(--bg-surface)] active:scale-[0.98]' : 'opacity-60'} border-[var(--border-primary)] bg-[var(--bg-surface-alt)]`}
                                >
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-[var(--text-muted)] text-tiny font-black uppercase tracking-widest">{t('upgrade.pricing.free_tier')}</h3>
                                        <span className="text-base font-black text-[var(--text-secondary)]">{t('upgrade.pricing.free_price')}</span>
                                    </div>
                                    <ul className="text-tiny text-[var(--text-muted)] space-y-1 flex-1">
                                        {freeFeatures.map(key => (
                                            <li key={key} className="flex gap-1.5"><Check size={12} className="text-emerald-500/50 shrink-0 mt-0.5" /> {t(`upgrade.pricing.features.${key}`)}</li>
                                        ))}
                                    </ul>
                                    {onSelectFree && (
                                        <button className="w-full mt-2 py-2.5 rounded-xl border border-[var(--border-secondary)] text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest hover:bg-[var(--bg-surface)] transition-all">
                                            {t('upgrade.pricing.start_now')}
                                        </button>
                                    )}
                                </div>

                                {/* Supporter Tier */}
                                <div
                                    onClick={() => { setSelectedPlan('supporter'); if (isIOS) { handleAppleSubscribe(); } else { setPendingChoice('paid'); } }}
                                    className={`p-4 md:p-5 rounded-2xl border flex flex-col gap-2 md:gap-3 relative cursor-pointer transition-all active:scale-[0.98] ${selectedPlan === 'supporter' ? 'border-emerald-500/30 bg-emerald-500/10 ring-2 ring-emerald-500/40' : 'border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10'}`}
                                >
                                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-3 py-0.5 rounded-full text-3xs font-black uppercase tracking-widest shadow-lg">
                                        {t('upgrade.pricing.recommended')}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-emerald-400 text-tiny font-black uppercase tracking-widest">{t('upgrade.pricing.paid_tier')}</h3>
                                        <span className="text-base font-black text-white">{priceLabel}</span>
                                    </div>

                                    <p className="text-2xs text-emerald-300/80 font-bold">{t('upgrade.pricing.features.all_free')}</p>

                                    <div className="flex-1 space-y-2">
                                        {paidCategories.map(cat => (
                                            <div key={cat.key}>
                                                <div className="flex items-center gap-1 mb-1">
                                                    <span className="text-emerald-400/70">{cat.icon}</span>
                                                    <span className="text-4xs font-black uppercase tracking-widest text-emerald-400/70">{t(`upgrade.pricing.categories.${cat.key}`)}</span>
                                                </div>
                                                <ul className="text-2xs text-[var(--text-secondary)] space-y-0.5">
                                                    {cat.features.map(key => (
                                                        <li key={key} className="flex gap-1.5"><Check size={11} className="text-emerald-400 shrink-0 mt-0.5" /> {t(`upgrade.pricing.features.${key}`)}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>

                                    <button className="w-full mt-2 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-2xs font-black uppercase tracking-widest shadow-lg transition-all flex items-center justify-center gap-2">
                                        <Sparkles size={13} /> {t('upgrade.pricing.btn_subscribe')}
                                    </button>
                                </div>

                                {/* Premium Tier */}
                                {premiumConfig && (
                                    <div
                                        onClick={() => { setSelectedPlan('premium'); if (isIOS) { handleAppleSubscribe(); } else { setPendingChoice('paid'); } }}
                                        className={`p-4 md:p-5 rounded-2xl border flex flex-col gap-2 md:gap-3 relative cursor-pointer transition-all active:scale-[0.98] ${selectedPlan === 'premium' ? 'border-indigo-500/30 bg-indigo-500/10 ring-2 ring-indigo-500/40' : 'border-indigo-500/20 bg-indigo-500/5 hover:bg-indigo-500/10'}`}
                                    >
                                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-3 py-0.5 rounded-full text-3xs font-black uppercase tracking-widest shadow-lg flex items-center gap-1">
                                            <Crown size={9} /> Premium
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-indigo-400 text-tiny font-black uppercase tracking-widest">Premium</h3>
                                            <span className="text-base font-black text-white">{premiumPriceLabel || `kr ${premiumConfig.priceNok}/mnd`}</span>
                                        </div>

                                        <p className="text-2xs text-indigo-300/80 font-bold">{t('upgrade.pricing.features.all_supporter')}</p>

                                        <div className="flex-1">
                                            <div className="flex items-center gap-1 mb-1">
                                                <span className="text-indigo-400/70"><Car size={13} /></span>
                                                <span className="text-4xs font-black uppercase tracking-widest text-indigo-400/70">{t('upgrade.pricing.categories.car_screen')}</span>
                                            </div>
                                            <ul className="text-2xs text-[var(--text-secondary)] space-y-0.5">
                                                {premiumConfig.features.includes('android_auto') && (
                                                    <li className="flex gap-1.5"><Check size={11} className="text-indigo-400 shrink-0 mt-0.5" /> {t("auto.upgradeoverlay.android_auto", "Android Auto")}</li>
                                                )}
                                                {premiumConfig.features.includes('carplay') && (
                                                    <li className="flex gap-1.5"><Check size={11} className="text-indigo-400 shrink-0 mt-0.5" /> {t("auto.upgradeoverlay.apple_carplay", "Apple CarPlay")}</li>
                                                )}
                                                <li className="flex gap-1.5"><Check size={11} className="text-indigo-400 shrink-0 mt-0.5" /> {t('upgrade.pricing.features.car_map')}</li>
                                                <li className="flex gap-1.5"><Check size={11} className="text-indigo-400 shrink-0 mt-0.5" /> {t('upgrade.pricing.features.car_navigation')}</li>
                                            </ul>
                                        </div>

                                        <button className="w-full mt-2 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-2xs font-black uppercase tracking-widest shadow-lg transition-all flex items-center justify-center gap-2">
                                            <Crown size={13} /> {t('upgrade.pricing.btn_subscribe_premium')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {onApplyTester && (
                                testerApplicationsEnabled ? (
                                    <div className="bg-amber-500/5 border border-amber-500/15 rounded-2xl p-3 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Sparkles size={14} className="text-amber-400" />
                                            <span className="text-2xs font-black text-amber-400 uppercase tracking-widest">
                                                {t('profile.membership_status.tester_program_title')}
                                            </span>
                                        </div>
                                        <p className="text-2xs text-[var(--text-secondary)] leading-relaxed">
                                            {t('profile.membership_status.tester_program_desc')}
                                        </p>
                                        <button
                                            onClick={() => { onClose(); onApplyTester(); }}
                                            className="w-full bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-400 py-2.5 rounded-xl text-3xs font-black uppercase tracking-widest transition-all"
                                        >
                                            {t('profile.membership_status.apply_tester')}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="bg-stone-500/5 border border-stone-500/10 rounded-2xl p-3 flex items-center gap-2">
                                        <Sparkles size={14} className="text-[var(--text-muted)] shrink-0" />
                                        <p className="text-2xs text-[var(--text-muted)] font-bold">{t('upgrade.tester.not_recruiting')}</p>
                                    </div>
                                )
                            )}
                        </div>

                        {/* Footer with legal links */}
                        <div className="shrink-0 px-4 md:px-8 pb-3 md:pb-5 pt-2 border-t border-[var(--border-primary)]" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0.75rem)' }}>
                            <div className="flex items-center justify-center gap-3 flex-wrap">
                                {isIOS ? (
                                    <>
                                        <button
                                            onClick={handleRestorePurchases}
                                            disabled={restoring}
                                            className="flex items-center gap-1 text-2xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] font-black uppercase tracking-widest transition-colors"
                                        >
                                            {restoring ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
                                            {t('upgrade.restore_purchases')}
                                        </button>
                                        {iapError && <p className="text-2xs text-red-400 font-medium w-full text-center">{iapError}</p>}
                                    </>
                                ) : (
                                    <p className="text-2xs text-[var(--text-muted)] font-medium">
                                        {t('upgrade.pricing.stripe_notice')}
                                    </p>
                                )}
                                <span className="text-[var(--text-muted)]">·</span>
                                <a href="/vilkar" target="_blank" rel="noopener noreferrer" className="text-2xs text-[var(--text-muted)] underline hover:text-[var(--text-secondary)] font-medium">{t('upgrade.legal.terms')}</a>
                                <span className="text-[var(--text-muted)]">·</span>
                                <a href="/personvern" target="_blank" rel="noopener noreferrer" className="text-2xs text-[var(--text-muted)] underline hover:text-[var(--text-secondary)] font-medium">{t('upgrade.legal.privacy')}</a>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default UpgradeOverlay;
